import "./Ticker.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";

const getMotionPreference = () =>
	window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

function Ticker({ sets, guesses, correctAnswer }) {
	const tickerRef = useRef(null);
	const latestSetRef = useRef(null);
	const dragRef = useRef(null);
	const [isDragging, setIsDragging] = useState(false);
	const guessedSetCodes = useMemo(() => new Set(guesses.map((guess) => guess.s)), [guesses]);
	const latestSetCode = guesses.length ? guesses[guesses.length - 1].s : null;
	const latestSetIndex = sets.findIndex((set) => set.code === latestSetCode);

	useEffect(() => {
		if (latestSetIndex < 0 || !latestSetRef.current) return;

		latestSetRef.current.scrollIntoView({
			behavior: getMotionPreference() ? "auto" : "smooth",
			block: "nearest",
			inline: "center",
		});
	}, [latestSetIndex]);

	const correctYear = correctAnswer.d.split("-")[0];
	const startDrag = (event) => {
		// Touchscreens already provide better native momentum scrolling. This
		// controller adds grab-to-scroll for mouse and pen input.
		if (event.pointerType === "touch") return;

		dragRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startScrollLeft: tickerRef.current.scrollLeft,
		};
		tickerRef.current.setPointerCapture?.(event.pointerId);
		setIsDragging(true);
	};
	const moveDrag = (event) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;

		event.preventDefault();
		tickerRef.current.scrollLeft = drag.startScrollLeft + drag.startX - event.clientX;
	};
	const stopDrag = (event) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;

		tickerRef.current.releasePointerCapture?.(event.pointerId);
		dragRef.current = null;
		setIsDragging(false);
	};

	return (
		<div
			className={`ticker${isDragging ? " ticker--dragging" : ""}`}
			ref={tickerRef}
			role='region'
			aria-label='Magic set timeline'
			tabIndex={0}
			onPointerDown={startDrag}
			onPointerMove={moveDrag}
			onPointerUp={stopDrag}
			onPointerCancel={stopDrag}>
			<div className='ticker__list'>
				{sets.map((set) => {
					const year = set.released_at.split("-")[0];
					let resultClass = "";

					if (guessedSetCodes.has(set.code)) {
						if (correctAnswer.s === set.code) resultClass = "guessed-correct";
						else if (correctYear === year) resultClass = "guessed-almost-correct";
						else resultClass = "guessed-wrong";
					}

					const title = `${set.name} (${set.code.toUpperCase()} - ${year})`;
					return (
						<div className='ticker__item' key={set.code} ref={set.code === latestSetCode ? latestSetRef : null}>
							<img draggable='false' className={`set-icon ${resultClass}`.trim()} alt={title} title={title} src={`${process.env.PUBLIC_URL}/img/${set.code}.svg`} />
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default Ticker;
